// src/pages/dashboards/practitioner/PractitionerClientDetail.jsx
// Read-only deep-dive into one client's deductions. Three tabs: Summary (category
// breakdown + chart), Expenses (filterable table), Receipts (list with image viewer).
// No edit controls anywhere - practitioners observe, never modify.

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as api from "../../../api/practitioners";
import { tokenStore } from "../../../api/client";
import { Card, Select, Badge, Button, Loading, EmptyState, Modal } from "../../../components/ui";
import { HBarChart } from "../../../components/charts";
import { Download } from "../../../components/icons";
import { money, date } from "../../../lib/format";
import { useToast } from "../../../context/ToastContext";

// SA tax year
const currentTaxYear = () => {
  const d = new Date();
  return d.getMonth() >= 2 ? d.getFullYear() + 1 : d.getFullYear();
};
const TY = currentTaxYear();
const TAX_YEARS = [TY, TY - 1, TY - 2];

const CAT_COLORS = ["#12B266", "#2AD17B", "#3B82F6", "#F5A623", "#8A938F", "#0EA5A0", "#A855F7", "#EF4444"];

const TABS = [
  { key: "summary", label: "Summary" },
  { key: "expenses", label: "Expenses" },
  { key: "receipts", label: "Receipts" },
];

export default function PractitionerClientDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [tab, setTab] = useState("summary");
  const [taxYear, setTaxYear] = useState(TY);

  // data
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [receipts, setReceipts] = useState(null);
  const [clientName, setClientName] = useState("");
  const [released, setReleased] = useState(false);

  // receipt detail modal
  const [viewReceipt, setViewReceipt] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptImg, setReceiptImg] = useState(null);

  // load summary + release status
  useEffect(() => {
    setSummary(null);
    api.getClientSummary(userId, { tax_year: taxYear })
      .then(setSummary)
      .catch((e) => toast.error(e.message));
    // check release status via dashboard
    api.getDashboard({ tax_year: taxYear }).then((d) => {
      const client = (d.clients || []).find((c) => c.user_id === userId);
      setReleased(client ? client.released : false);
      if (client && !clientName) setClientName(client.name);
    }).catch(() => setReleased(false));
  }, [userId, taxYear]);

  // load expenses when that tab is active
  useEffect(() => {
    if (tab !== "expenses") return;
    setExpenses(null);
    api.getClientExpenses(userId, { tax_year: taxYear })
      .then(setExpenses)
      .catch((e) => { toast.error(e.message); setExpenses([]); });
  }, [userId, taxYear, tab]);

  // load receipts when that tab is active
  useEffect(() => {
    if (tab !== "receipts") return;
    setReceipts(null);
    api.getClientReceipts(userId)
      .then(setReceipts)
      .catch((e) => { toast.error(e.message); setReceipts([]); });
  }, [userId, tab]);

  // download tax pack
  const downloadPack = async () => {
    try {
      const blob = await api.downloadClientTaxPack(userId, taxYear);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TaxPack-${(clientName || "Client").replace(/\s+/g, "_")}-TY${taxYear}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Tax pack downloaded");
    } catch (e) {
      toast.error(e.message);
    }
  };

  // open receipt detail
  const openReceipt = useCallback(async (receiptId) => {
    setViewReceipt(receiptId);
    setReceiptData(null);
    setReceiptImg(null);
    try {
      const data = await api.getClientReceipt(userId, receiptId);
      setReceiptData(data);
    } catch (e) { toast.error(e.message); }
    // fetch image as blob (authenticated)
    try {
      const res = await fetch(api.clientReceiptImageUrl(userId, receiptId), {
        headers: { Authorization: `Bearer ${tokenStore.access}` },
      });
      if (res.ok) setReceiptImg(URL.createObjectURL(await res.blob()));
    } catch { /* no image */ }
  }, [userId]);

  // cleanup blob URL
  useEffect(() => {
    return () => { if (receiptImg) URL.revokeObjectURL(receiptImg); };
  }, [receiptImg]);

  const cats = summary
    ? (summary.categories || [])
        .map((c, i) => ({ label: c.name, value: Number(c.total || 0), color: CAT_COLORS[i % CAT_COLORS.length] }))
        .filter((c) => c.value > 0)
    : [];

  return (
    <div>
      {/* header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/practitioner")} className="text-tb-muted hover:text-tb-ink dark:hover:text-tb-d-text transition-colors text-sm">
          {"\u2190"} Clients
        </button>
        <span className="text-tb-muted">/</span>
        <h1 className="text-2xl font-display font-bold truncate">{clientName || "Client"}</h1>
        <div className="ml-auto shrink-0 flex items-center gap-2">
          {released && (
            <Button onClick={downloadPack} className="inline-flex items-center gap-1.5">
              <Download size={16} /> Tax pack
            </Button>
          )}
          {!released && summary && (
            <Badge tone="amber">Tax pack not released</Badge>
          )}
          <Select value={taxYear} onChange={(e) => setTaxYear(parseInt(e.target.value, 10))} className="w-auto">
            {TAX_YEARS.map((y) => <option key={y} value={y}>TY {y}</option>)}
          </Select>
        </div>
      </div>

      {/* stat tiles */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <Card className="px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-tb-muted">Total claimed</p>
            <p className="text-xl font-bold mt-1 tabular-nums text-tb-brand">{money(summary.grand_total)}</p>
          </Card>
          <Card className="px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-tb-muted">Categories</p>
            <p className="text-xl font-bold mt-1 tabular-nums">{cats.length}</p>
          </Card>
          <Card className="px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-tb-muted">Items</p>
            <p className="text-xl font-bold mt-1 tabular-nums">
              {(summary.categories || []).reduce((s, c) => s + c.count, 0)}
            </p>
          </Card>
          <Card className="px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-tb-muted">Tax year</p>
            <p className="text-xl font-bold mt-1 tabular-nums">{taxYear}</p>
          </Card>
        </div>
      )}

      {/* tabs */}
      <div className="flex gap-1 mb-5 border-b border-tb-border dark:border-tb-d-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? "border-tb-brand text-tb-brand"
                : "border-transparent text-tb-muted hover:text-tb-ink dark:hover:text-tb-d-text"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* tab content */}
      {tab === "summary" && <SummaryTab summary={summary} cats={cats} />}
      {tab === "expenses" && <ExpensesTab expenses={expenses} onViewReceipt={openReceipt} />}
      {tab === "receipts" && <ReceiptsTab receipts={receipts} onView={openReceipt} />}

      {/* receipt detail modal */}
      <Modal open={!!viewReceipt} onClose={() => setViewReceipt(null)} title="Receipt" width="max-w-3xl">
        <ReceiptDetail data={receiptData} imgSrc={receiptImg} />
      </Modal>
    </div>
  );
}

// -- Summary tab ---------------------------------------------------------------
function SummaryTab({ summary, cats }) {
  if (!summary) return <Loading label="Loading summary" />;
  if (cats.length === 0) return <EmptyState title="No deductions" hint="This client has no claimed expenses for this tax year." />;

  return (
    <Card className="p-6">
      <p className="text-sm font-semibold mb-4">Deductions by category</p>
      <HBarChart data={cats} valueFormat={money} />

      {/* category table */}
      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-tb-muted border-b border-tb-border dark:border-tb-d-border">
              <th className="pb-2 font-medium">Category</th>
              <th className="pb-2 font-medium text-right">Items</th>
              <th className="pb-2 font-medium text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-tb-border dark:divide-tb-d-border">
            {(summary.categories || []).filter((c) => c.count > 0).map((c, i) => (
              <tr key={c.id}>
                <td className="py-2.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full mr-2" style={{ backgroundColor: CAT_COLORS[i % CAT_COLORS.length] }} />
                  {c.name}
                </td>
                <td className="py-2.5 text-right tabular-nums text-tb-muted">{c.count}</td>
                <td className="py-2.5 text-right tabular-nums font-medium">{money(c.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-tb-border dark:border-tb-d-border font-bold">
              <td className="pt-3">Total</td>
              <td className="pt-3 text-right tabular-nums">
                {(summary.categories || []).reduce((s, c) => s + c.count, 0)}
              </td>
              <td className="pt-3 text-right tabular-nums text-tb-brand">{money(summary.grand_total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}

// -- Expenses tab --------------------------------------------------------------
function ExpensesTab({ expenses, onViewReceipt }) {
  if (!expenses) return <Loading label="Loading expenses" />;
  if (expenses.length === 0) return <EmptyState title="No expenses" hint="No claimed expenses for this tax year." />;

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-tb-muted bg-tb-bg-2 dark:bg-tb-d-inset">
              <th className="px-4 py-2.5 font-medium">Date</th>
              <th className="px-4 py-2.5 font-medium">Merchant</th>
              <th className="px-4 py-2.5 font-medium hidden sm:table-cell">Description</th>
              <th className="px-4 py-2.5 font-medium hidden md:table-cell">Category</th>
              <th className="px-4 py-2.5 font-medium text-right">Amount</th>
              <th className="px-4 py-2.5 font-medium text-right w-20">Receipt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-tb-border dark:divide-tb-d-border">
            {expenses.map((e) => (
              <tr key={e.id} className="hover:bg-tb-bg-2/50 dark:hover:bg-tb-d-inset/50">
                <td className="px-4 py-2.5 whitespace-nowrap">{date(e.expense_date)}</td>
                <td className="px-4 py-2.5 truncate max-w-[180px]">{e.merchant || "—"}</td>
                <td className="px-4 py-2.5 truncate max-w-[200px] hidden sm:table-cell text-tb-muted">{e.description || "—"}</td>
                <td className="px-4 py-2.5 hidden md:table-cell">
                  {e.category_name && <Badge tone="green">{e.category_name}</Badge>}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums font-medium">{money(e.amount)}</td>
                <td className="px-4 py-2.5 text-right">
                  {e.receipt_id ? (
                    <button
                      onClick={() => onViewReceipt(e.receipt_id)}
                      className="text-xs text-tb-brand hover:underline"
                    >
                      View
                    </button>
                  ) : (
                    <span className="text-xs text-tb-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 text-xs text-tb-muted border-t border-tb-border dark:border-tb-d-border">
        {expenses.length} expense{expenses.length !== 1 ? "s" : ""} · read-only
      </div>
    </Card>
  );
}

// -- Receipts tab --------------------------------------------------------------
function ReceiptsTab({ receipts, onView }) {
  if (!receipts) return <Loading label="Loading receipts" />;
  if (receipts.length === 0) return <EmptyState title="No receipts" hint="This client hasn't scanned any receipts yet." />;

  return (
    <Card className="divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
      {receipts.map((r) => (
        <button
          key={r.id}
          onClick={() => onView(r.id)}
          className="w-full px-4 sm:px-5 py-3.5 flex items-center gap-4 text-left hover:bg-tb-bg-2/50 dark:hover:bg-tb-d-inset/50 transition-colors"
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{r.merchant || "Unknown merchant"}</p>
            <p className="text-xs text-tb-muted">
              {date(r.receipt_date)} · {r.item_count} item{r.item_count !== 1 ? "s" : ""}
            </p>
          </div>
          <span className="text-sm font-medium tabular-nums shrink-0">{money(r.total)}</span>
        </button>
      ))}
    </Card>
  );
}

// -- Receipt detail (inside modal) --------------------------------------------
function ReceiptDetail({ data, imgSrc }) {
  if (!data) return <Loading label="Loading receipt" />;

  const items = data.items || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold">{data.merchant || "Receipt"}</span>
          <span className="text-sm text-tb-muted">{date(data.receipt_date)}</span>
        </div>
        <div className="rounded-xl border border-tb-border dark:border-tb-d-border divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="min-w-0 truncate">{it.label || it.raw_text || "Item"}</span>
              <span className="tabular-nums shrink-0 text-tb-muted">{money(it.amount)}</span>
            </div>
          ))}
          {items.length === 0 && <p className="px-3 py-3 text-sm text-tb-muted">No line items.</p>}
        </div>
        <p className="mt-3 text-right text-lg font-bold tabular-nums">{money(data.total)}</p>
      </div>
      <div>
        {imgSrc ? (
          <img src={imgSrc} alt="Receipt" className="w-full rounded-xl border border-tb-border dark:border-tb-d-border" />
        ) : (
          <div className="grid place-items-center h-48 rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset text-sm text-tb-muted">
            No image available
          </div>
        )}
      </div>
    </div>
  );
}